import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, CheckCircle2, XCircle, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const DOC_TYPES = ["visit_ticket", "quote", "invoice", "receipt", "contract", "purchase_order"];
const STATUSES = ["pending", "paid", "cancelled"];

export default function Invoices() {
  const { t, lang } = useI18n();
  const [docs, setDocs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    doc_type: "quote",
    client_name: "",
    client_contact: "",
    notes: "",
    items: [{ label: "", quantity: 1, unit_price: 0, total: 0 }],
  });

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (typeFilter !== "all") params.doc_type = typeFilter;
    const { data } = await api.get("/invoices", { params });
    setDocs(data);
  }, [statusFilter, typeFilter]);
  useEffect(() => { load(); }, [load]);

  const fmt = (n) => new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

  const setItem = (idx, k, v) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [k]: v };
    if (k === "quantity" || k === "unit_price") {
      items[idx].total = Number(items[idx].quantity || 0) * Number(items[idx].unit_price || 0);
    }
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { label: "", quantity: 1, unit_price: 0, total: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const total = form.items.reduce((s, i) => s + Number(i.total || 0), 0);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", form);
      toast.success("Document créé");
      setNewOpen(false);
      setForm({ doc_type: "quote", client_name: "", client_contact: "", notes: "", items: [{ label: "", quantity: 1, unit_price: 0, total: 0 }] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/invoices/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-invoices">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Facturation</div>
          <h1 className="mt-2 font-display text-4xl text-brand-navy tracking-tight">{t("admin.invoices")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="inv-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              {DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setNewOpen(true)} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="new-invoice-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouveau document
          </Button>
        </div>
      </div>

      <Card>
        <Table data-testid="invoices-table">
          <TableHeader>
            <TableRow>
              <TableHead>Numéro</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-12">Aucun document.</TableCell></TableRow>
            ) : docs.map((d) => (
              <TableRow key={d.id} data-testid={`invoice-row-${d.id}`}>
                <TableCell className="font-mono text-sm font-semibold text-brand-navy">{d.number}</TableCell>
                <TableCell><span className="text-xs uppercase tracking-widest px-2 py-1 rounded bg-slate-100 text-slate-700">{d.doc_type}</span></TableCell>
                <TableCell>
                  <div className="text-sm font-semibold text-brand-navy">{d.client_name}</div>
                  <div className="text-xs text-slate-500">{d.client_contact}</div>
                </TableCell>
                <TableCell className="font-bold text-brand-navy">{fmt(d.total)}</TableCell>
                <TableCell>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${d.status === "paid" ? "bg-emerald-50 text-emerald-700" : d.status === "cancelled" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{d.status}</span>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setDetail(d)} data-testid={`inv-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
                    {d.status !== "paid" && (
                      <Button size="icon" variant="ghost" onClick={() => setStatus(d.id, "paid")} title="Marquer payé" data-testid={`inv-pay-${d.id}`}>
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </Button>
                    )}
                    {d.status !== "cancelled" && (
                      <Button size="icon" variant="ghost" onClick={() => setStatus(d.id, "cancelled")} title="Annuler" data-testid={`inv-cancel-${d.id}`}>
                        <XCircle className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="invoice-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{detail.number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><Label>Type</Label><div className="mt-1 uppercase text-xs tracking-widest">{detail.doc_type}</div></div>
                  <div><Label>Statut</Label><div className="mt-1 uppercase text-xs tracking-widest">{detail.status}</div></div>
                  <div><Label>Client</Label><div className="mt-1">{detail.client_name}</div></div>
                  <div><Label>Contact</Label><div className="mt-1">{detail.client_contact}</div></div>
                </div>
                <div>
                  <Label>Lignes</Label>
                  <Table className="mt-1">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead>Qté</TableHead>
                        <TableHead>PU</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(detail.items || []).map((it, i) => (
                        <TableRow key={i}>
                          <TableCell>{it.label}</TableCell>
                          <TableCell>{it.quantity}</TableCell>
                          <TableCell>${it.unit_price}</TableCell>
                          <TableCell className="font-semibold">${it.total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="pt-3 border-t flex justify-between font-display text-xl">
                  <span>Total</span>
                  <span className="font-bold text-brand-navy">{fmt(detail.total)}</span>
                </div>
                {detail.notes && <div><Label>Notes</Label><div className="mt-1 text-sm text-slate-700">{detail.notes}</div></div>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New document dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-invoice-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nouveau document</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger data-testid="new-inv-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Client</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="new-inv-client" required /></div>
              <div className="col-span-2"><Label>Contact</Label><Input value={form.client_contact} onChange={(e) => setForm({ ...form, client_contact: e.target.value })} data-testid="new-inv-contact" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Lignes</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-6" placeholder="Description" value={it.label} onChange={(e) => setItem(i, "label", e.target.value)} required />
                    <Input className="col-span-2" type="number" placeholder="Qté" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="PU" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                    <div className="col-span-1 flex items-center justify-end font-semibold">${it.total}</div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} className="col-span-1"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t flex justify-between font-display text-lg">
                <span>Total</span>
                <span className="font-bold text-brand-navy">${total.toFixed(2)}</span>
              </div>
            </div>

            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="new-inv-submit">Créer le document</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
